import React, { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { NexOsProvider } from "@/lib/nexOs";
import { FloatingWindowsProvider } from "@/lib/floatingWindows";
import { NdsEditorProvider } from "@/lib/ndsEditor";
import { OpsProvider } from "@/lib/opsCards";
import { DemoModeProvider } from "@/lib/demoMode";
import WindowLayer, { KIND_MAP, subtitleFor, titleFor } from "@/components/nds/windows/WindowLayer";

// Payload handed over by popOutWindow(); kept in sessionStorage so a refresh of this tab still works.
const readPayload = (kind, key) => {
  const sKey = `nex_tab_${kind}_${key}`;
  try {
    const fresh = key && localStorage.getItem(`nex_popout_${key}`);
    if (fresh) {
      localStorage.removeItem(`nex_popout_${key}`);
      sessionStorage.setItem(sKey, fresh);
    }
    return JSON.parse(sessionStorage.getItem(sKey) || "{}").payload || {};
  } catch {
    return {};
  }
};

export default function WindowTabPage() {
  const { kind } = useParams();
  const [sp] = useSearchParams();
  const win = useMemo(() => ({ id: "tab-main", kind, payload: readPayload(kind, sp.get("k")) }), [kind, sp]);
  const entry = KIND_MAP[kind];
  if (entry) document.title = `${titleFor(win)} · TEA`;

  return (
    <DemoModeProvider value={false}>
      <NexOsProvider>
        <FloatingWindowsProvider>
          <NdsEditorProvider>
            <OpsProvider>
              <div className="nds-scope min-h-screen bg-[#0B1220] text-nex-silver flex flex-col" data-testid={`nds-window-tab-${kind}`}>
                <header className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0B1220]/95 backdrop-blur pt-[max(0.75rem,env(safe-area-inset-top))]">
                  <a href="/admin" data-testid="nds-window-tab-home" className="inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-nex-chrome hover:text-nex-gold">
                    <ArrowLeft className="w-4 h-4" /> Main board
                  </a>
                  <div className="min-w-0">
                    <div className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-gold uppercase truncate">{entry?.kicker || "// WINDOW"}</div>
                    <h1 data-testid="nds-window-tab-title" className="font-semibold text-base truncate">{entry ? titleFor(win) : "This screen can't open in its own tab"}</h1>
                    {entry && subtitleFor(win) && <p className="text-[0.7rem] text-nex-muted truncate">{subtitleFor(win)}</p>}
                  </div>
                </header>
                <main className="flex-1 w-full max-w-[1400px] mx-auto">{entry ? <entry.Content win={win} /> : null}</main>
                <WindowLayer baseTitle={entry ? titleFor(win) : "Window tab"} />
              </div>
            </OpsProvider>
          </NdsEditorProvider>
        </FloatingWindowsProvider>
      </NexOsProvider>
    </DemoModeProvider>
  );
}
